import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * Sprint 12 — Reemplazo de window.confirm / window.alert nativos.
 *
 * Cierra C11: los diálogos nativos de Win32 bloquean el renderer Electron
 * y son inaccesibles. Este provider expone hooks imperativos `useConfirm`
 * y `useAlert` que devuelven promesas, equivalentes en uso a window.confirm,
 * pero con UI propia del POS: Escape cierra, focus trap, botones ≥44px,
 * branding consistente.
 */

export type ConfirmVariant = 'info' | 'warn' | 'destructive' | 'success';

interface ConfirmOptions {
  titulo: string;
  mensaje: string;
  textoConfirmar?: string;
  textoCancelar?: string;
  variant?: ConfirmVariant;
}

interface AlertOptions {
  titulo: string;
  mensaje: string;
  textoCerrar?: string;
  variant?: ConfirmVariant;
}

interface DialogsContextType {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  alert: (opts: AlertOptions) => Promise<void>;
}

const DialogsContext = createContext<DialogsContextType | null>(null);

interface DialogState {
  tipo: 'confirm' | 'alert';
  opts: ConfirmOptions | AlertOptions;
  resolve: (v: boolean) => void;
}

export function DialogsProvider({ children }: { children: React.ReactNode }) {
  const [estado, setEstado] = useState<DialogState | null>(null);
  const resolverRef = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback(
    (opts: ConfirmOptions): Promise<boolean> =>
      new Promise<boolean>((resolve) => {
        resolverRef.current = resolve;
        setEstado({ tipo: 'confirm', opts, resolve });
      }),
    [],
  );

  const alert = useCallback(
    (opts: AlertOptions): Promise<void> =>
      new Promise<void>((resolve) => {
        resolverRef.current = () => resolve();
        setEstado({
          tipo: 'alert',
          opts,
          resolve: () => resolve(),
        });
      }),
    [],
  );

  // Sprint 12 — expone helpers globales en window para reemplazo gradual de
  // los `window.confirm/alert` nativos sin imports en cada componente.
  // Uso:
  //   const ok = await window.confirmar({ titulo: '¿', mensaje: 'X' });
  //   await window.alertar({ titulo: 'Aviso', mensaje: 'X' });
  // Compat: también admiten un string directo como mensaje.
  useEffect(() => {
    const w = window as unknown as {
      confirmar: (msg: string | ConfirmOptions) => Promise<boolean>;
      alertar: (msg: string | AlertOptions) => Promise<void>;
    };
    w.confirmar = (arg) =>
      confirm(typeof arg === 'string' ? { titulo: '¿Estás seguro?', mensaje: arg } : arg);
    w.alertar = (arg) =>
      alert(typeof arg === 'string' ? { titulo: 'Aviso', mensaje: arg } : arg);
  }, [confirm, alert]);

  const cerrar = useCallback((valor: boolean) => {
    if (resolverRef.current) {
      resolverRef.current(valor);
      resolverRef.current = null;
    }
    setEstado(null);
  }, []);

  return (
    <DialogsContext.Provider value={{ confirm, alert }}>
      {children}
      {estado && <DialogModal estado={estado} cerrar={cerrar} />}
    </DialogsContext.Provider>
  );
}

export function useConfirm(): DialogsContextType['confirm'] {
  const ctx = useContext(DialogsContext);
  if (!ctx) throw new Error('useConfirm fuera de DialogsProvider');
  return ctx.confirm;
}

export function useAlert(): DialogsContextType['alert'] {
  const ctx = useContext(DialogsContext);
  if (!ctx) throw new Error('useAlert fuera de DialogsProvider');
  return ctx.alert;
}

// ============================================================================
// Modal interno con focus trap + Escape
// ============================================================================

function DialogModal({
  estado,
  cerrar,
}: {
  estado: DialogState;
  cerrar: (v: boolean) => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previoFocoRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previoFocoRef.current = document.activeElement as HTMLElement | null;
    const enfocable = dialogRef.current?.querySelector<HTMLElement>(
      'button[data-autofocus="true"], button',
    );
    enfocable?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cerrar(false);
        return;
      }
      if (e.key === 'Tab') {
        // Focus trap simple: tab cicla entre los botones del diálogo.
        const focusables = dialogRef.current?.querySelectorAll<HTMLElement>('button');
        if (!focusables || focusables.length === 0) return;
        const lista = Array.from(focusables);
        const activo = document.activeElement as HTMLElement | null;
        const idx = activo ? lista.indexOf(activo) : -1;
        e.preventDefault();
        const siguiente = e.shiftKey
          ? lista[(idx - 1 + lista.length) % lista.length]
          : lista[(idx + 1) % lista.length];
        siguiente?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previoFocoRef.current?.focus?.();
    };
  }, [cerrar]);

  const variant = estado.opts.variant ?? 'info';
  const iconoYColor = {
    info: { icono: 'ⓘ', color: 'text-blue-600' },
    warn: { icono: '⚠', color: 'text-amber-600' },
    destructive: { icono: '✕', color: 'text-red-600' },
    success: { icono: '✓', color: 'text-green-600' },
  }[variant];

  const textoConf = (estado.opts as ConfirmOptions).textoConfirmar ?? 'Aceptar';
  const textoCanc = (estado.opts as ConfirmOptions).textoCancelar ?? 'Cancelar';
  const textoCerrar = (estado.opts as AlertOptions).textoCerrar ?? 'Entendido';

  const btnConfClass =
    variant === 'destructive'
      ? 'bg-red-600 hover:bg-red-700 text-white'
      : variant === 'warn'
      ? 'bg-amber-600 hover:bg-amber-700 text-white'
      : 'bg-primary hover:opacity-90 text-white';

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-titulo"
      aria-describedby="dialog-mensaje"
    >
      <div
        ref={dialogRef}
        className="w-full max-w-md rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-slate-200 p-5">
          <div className="flex items-start gap-3">
            <span className={`text-2xl ${iconoYColor.color}`} aria-hidden>
              {iconoYColor.icono}
            </span>
            <div className="flex-1">
              <h2 id="dialog-titulo" className="text-lg font-semibold text-slate-900">
                {estado.opts.titulo}
              </h2>
              <p id="dialog-mensaje" className="mt-2 whitespace-pre-line text-sm text-slate-700">
                {estado.opts.mensaje}
              </p>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 bg-slate-50 p-4 rounded-b-xl">
          {estado.tipo === 'confirm' && (
            <button
              type="button"
              className="min-h-[44px] rounded-lg border border-slate-300 bg-white px-5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
              onClick={() => cerrar(false)}
            >
              {textoCanc}
            </button>
          )}
          <button
            type="button"
            data-autofocus="true"
            className={`min-h-[44px] rounded-lg px-5 py-2 text-sm font-medium ${btnConfClass}`}
            onClick={() => cerrar(true)}
          >
            {estado.tipo === 'confirm' ? textoConf : textoCerrar}
          </button>
        </div>
      </div>
    </div>
  );
}
